import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { College } from "@/hooks/useCollegeSearch";
import { Bookmark, ExternalLink, AlertCircle, MapPin, DollarSign, Church, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
// Legacy RecruitLook function — stubbed for RLevents
const toggleSaveCollege = async (_collegeId: string, _userId: string) => ({ saved: false });
import { useMembershipAccess } from "@/hooks/useMembershipAccess";
import { useNavigate } from "react-router-dom";
import { CollegeLogo } from "./CollegeLogo";

interface CollegeCardProps {
  college: College & { isSaved?: boolean };
  onClick?: () => void;
  onToggleSave?: (collegeId: string) => void;
  onSuggestCorrection?: (collegeId: string) => void;
  showActions?: boolean;
}

const CollegeCard: React.FC<CollegeCardProps> = ({ 
  college, 
  onClick, 
  onToggleSave, 
  onSuggestCorrection,
  showActions = false 
}) => {
  const { profile } = useAuth();
  const { hasAccessTo } = useMembershipAccess();
  const navigate = useNavigate();
  const isPremium = hasAccessTo('college-search');
  const formatCurrency = (value: number | null) => {
    if (value === null) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  const handleSaveToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!profile) {
      toast.error("You must be logged in to save colleges.");
      navigate('/auth');
      return;
    }

    if (!isPremium) {
      toast.error("Saving colleges is a premium feature. Upgrade to save colleges!", {
        action: {
          label: "Upgrade",
          onClick: () => navigate('/athlete-dashboard/membership')
        }
      });
      return;
    }

    // If the parent component provides an onToggleSave handler, use it
    // This allows compatibility with existing code
    if (onToggleSave) {
      onToggleSave(college.id);
      return;
    }
    
    // Otherwise use our direct function
    try {
      // Update UI optimistically
      const isCurrentlySaved = college.isSaved || false;
      college.isSaved = !isCurrentlySaved;
      
      // Call the toggle function directly
      const result: any = await toggleSaveCollege(college.id, profile.id);
      
      if (result && result.success === false) {
        // Revert optimistic update on failure
        college.isSaved = isCurrentlySaved;
        toast.error("Failed to update saved college.");
      } else {
        if (!isCurrentlySaved) {
          toast.success(`${college.name} added to your tracking list!`);
        } else {
          toast.success(`${college.name} removed from your tracking list.`);
        }
      }
    } catch (err) {
      college.isSaved = !college.isSaved;
      toast.error("An unexpected error occurred.");
    }
  };

  return (
    <Card 
      className="overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 h-full flex flex-col bg-white border border-gray-200 rounded-xl group shadow-md"
      onClick={onClick}
    >
      <CardContent className="p-0 flex-grow flex flex-col">
        {/* Hero section with logo - plain white background */}
        <div className="relative bg-white p-6 flex items-center justify-center min-h-[120px] border-b border-gray-100">
          <CollegeLogo logoUrl={college.school_logo} collegeName={college.name} size="xl" />
          {/* Division badge positioned in corner */}
          {college.division && (
            <span className="absolute top-3 right-3 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold shadow-sm">
              {college.division}
            </span>
          )}
        </div>
        
        {/* Content section */}
        <div className="p-5 flex-grow flex flex-col">
          {/* Type badges row */}
          <div className="flex items-center flex-wrap gap-1.5 mb-3">
            {college.public_private && (
              <span className="px-2.5 py-1 bg-blue-500 text-white rounded-full text-xs font-semibold">
                {college.public_private}
              </span>
            )}
            {college.religious_affiliation && (
              <div className="flex items-center px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-semibold">
                <Church size={12} className="mr-1" />
                <span>{college.religious_affiliation}</span>
              </div>
            )}
            {college.conference && (
              <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                {college.conference}
              </span>
            )}
          </div>
          
          {/* College name - bold and prominent */}
          <h3 className="text-xl font-bold text-foreground leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {college.name}
          </h3>
          
          {/* Location with icon */}
          <div className="flex items-center text-muted-foreground mb-4">
            <MapPin size={16} className="mr-1.5 flex-shrink-0 text-primary/70" />
            <span className="text-sm font-medium">{college.city}, {college.state}</span>
          </div>
          
          {/* Tuition section */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-lg text-center">
              <div className="text-xs text-emerald-600 font-medium mb-0.5">In-State</div>
              <div className="flex items-center justify-center text-emerald-800 font-bold">
                <DollarSign size={14} className="mr-0.5" />
                <span className="text-sm">
                  {college.in_state_tuition ? formatCurrency(college.in_state_tuition).replace('$', '') : 'N/A'}
                </span>
              </div>
            </div>
            <div className="bg-teal-50 border border-teal-100 p-3 rounded-lg text-center">
              <div className="text-xs text-teal-600 font-medium mb-0.5">Out-of-State</div>
              <div className="flex items-center justify-center text-teal-800 font-bold">
                <DollarSign size={14} className="mr-0.5" />
                <span className="text-sm">
                  {college.out_state_tuition ? formatCurrency(college.out_state_tuition).replace('$', '') : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Key Metrics Row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center bg-slate-50 p-2 rounded-lg border border-slate-100">
              <span className="text-[10px] text-slate-500 block uppercase tracking-wide font-medium">Admission</span>
              <span className="text-sm font-bold text-slate-800">
                {college.admission_rate !== null ? `${college.admission_rate}%` : 'N/A'}
              </span>
            </div>
            <div className="text-center bg-slate-50 p-2 rounded-lg border border-slate-100">
              <span className="text-[10px] text-slate-500 block uppercase tracking-wide font-medium">Graduation</span>
              <span className="text-sm font-bold text-slate-800">
                {college.graduation_rate !== null ? `${college.graduation_rate}%` : 'N/A'}
              </span>
            </div>
            <div className="text-center bg-slate-50 p-2 rounded-lg border border-slate-100">
              <span className="text-[10px] text-slate-500 block uppercase tracking-wide font-medium">Enrollment</span>
              <span className="text-sm font-bold text-slate-800">
                {college.undergraduate_enrollment ? college.undergraduate_enrollment.toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
          
          {/* Spacer to push buttons to bottom */}
          <div className="flex-grow" />
        </div>
        
        {/* Action Buttons */}
        <div className="px-5 pb-5 mt-auto space-y-2">
          <Button
            className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm"
            onClick={(e) => {
              e.stopPropagation();
              onClick && onClick();
            }}
          >
            VIEW DETAILS
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 border-gray-200 text-gray-600 hover:bg-gray-50 font-medium"
              onClick={handleSaveToggle}
            >
              <Bookmark 
                className="h-4 w-4 mr-1.5" 
                fill={college.isSaved ? "currentColor" : "none"}
              />
              {college.isSaved ? "Saved" : "Save"}
            </Button>
            
            {onSuggestCorrection && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-9 border-gray-200 text-gray-600 hover:bg-gray-50 font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  onSuggestCorrection(college.id);
                }}
              >
                <AlertCircle className="h-4 w-4 mr-1.5" />
                Suggest Fix
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CollegeCard;
